<script setup>
import { i18n } from 'boot/i18n';
import {
  HrIcon,
  HrItemLabel,
  HrOverflowMenuBtn,
  HrSortableItem,
  HrSortableList,
  HrTooltip,
} from 'src/design-system';
import { computed } from 'vue';

import { billingOrderNumbers } from '../../domain/services/coverageBilling';
import { todayDateStr } from '../../domain/services/effectiveDating';
import { presentCoverageRow } from '../presenters/coverageRowPresenter';
import CoverageRankChip from './CoverageRankChip.vue';

/**
 * The coordination-of-benefits sequence — the client's coverages in the order they are billed.
 *
 * Every coverage we are billing on, plus every one that could become billable: upcoming, on hold,
 * or awaiting eligibility verification. All rows are draggable and all hold a position — the
 * dividing line against the ended list is "can this ever be billed again", and only a lapsed
 * period settles that permanently.
 *
 * POSITION AND NUMBERING ARE SEPARATE. Every row holds a position (`cobRank`, what dragging
 * moves), and the arrangement IS the order — there is no separate confirmation step. The chip's
 * number is derived over the billable subset only, so upcoming, held, and unverified rows are
 * skipped in the numbering while still holding their place: drag one and you can see where it will
 * land once it becomes billable. Do not renumber to include them, and do not lock them out of
 * dragging.
 *
 * The chip sits in the TRAILING cluster rather than beside the drag handle: the leading section is
 * a fixed 40px auxiliary column sized for a single 24dp icon, so a handle and a chip cannot both
 * live there. Within that cluster it comes AFTER the status text, so it is the last thing before
 * the fixed-width overflow menu — status labels vary in width, and a chip placed before them would
 * shift horizontally from row to row instead of reading as a column.
 */
defineOptions({ name: 'CoverageList' });

const props = defineProps({
  /** Ranked coverages, already in billing order. */
  coverages: {
    type: Array,
    default: () => [],
  },
  /**
   * Whoever holds a policy on these coverages, keyed by person id. A row reads its own out of this
   * rather than fetching one, which is what keeps a list of rows to a single read.
   */
  people: {
    type: Object,
    default: () => ({}),
  },
  /** Funding-source refs, for resolving payer names. */
  fundingSources: {
    type: Array,
    default: () => [],
  },
  /** Coverage ids with billing paused — a separate aggregate, not a field on the coverage. */
  pausedCoverageIds: {
    type: Set,
    default: () => new Set(),
  },
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  edit: null,
  remove: null,
  /** Payload is the reordered array of coverage ids. */
  reorder: null,
  /** Payload is { id, offset } — the keyboard-accessible companion to dragging. */
  move: null,
  /** Payload is { coverageId, verified } — record an eligibility verdict. */
  verify: null,
  /** Payload is { coverageId, paused } — pause or release billing on a coverage. */
  pause: null,
});

const asOf = todayDateStr();

const fundingSourceNameById = computed(() => {
  // Keyed by the PAYER — a coverage stores `fundingSourceId`, so that is what the lookup needs.
  return new Map(props.fundingSources
    .map((fundingSource) => [fundingSource.fundingSourceId, fundingSource.name]));
});

const hasMultiple = computed(() => props.coverages.length > 1);

/** id → 1-based number, over the billable subset only. Absent means the row shows a placeholder. */
const orderNumbers = computed(() => {
  return billingOrderNumbers(props.coverages, asOf, props.pausedCoverageIds);
});

const rows = computed(() => {
  return props.coverages.map((coverage, index) => ({
    ...presentCoverageRow(coverage, {
      fundingSourceNameById: fundingSourceNameById.value,
      asOf,
      orderNumber: orderNumbers.value.get(coverage.id) ?? null,
      isBillingPaused: props.pausedCoverageIds.has(coverage.id),
      // A self-policyholder coverage names nobody, and the person whose details a payer wants is the
      // client. `clientId` is on the coverage, so the row can resolve that without being told twice.
      policyholderDemographics:
        props.people[coverage.policyholder.personId ?? coverage.clientId] ?? null,
    }),
    isFirst: index === 0,
    isLast: index === props.coverages.length - 1,
  }));
});

function menuOptions(row) {
  const options = [
    { label: i18n.t('common.buttonEdit'), icon: 'edit', value: 'edit' },
  ];

  // The two stored decisions, each as a plain toggle — they are independent, so both are always
  // offered rather than one masking the other.
  options.push(row.coverage.eligibilityVerification?.verified === true
    ? { label: i18n.t('coverage.actionMarkUnverified'), icon: 'remove_done', value: 'unverify' }
    : { label: i18n.t('coverage.actionMarkVerified'), icon: 'task_alt', value: 'verify' });

  options.push(props.pausedCoverageIds.has(row.coverage.id)
    ? { label: i18n.t('coverage.actionReleaseHold'), icon: 'play_circle', value: 'release' }
    : { label: i18n.t('coverage.actionPutOnHold'), icon: 'pause_circle', value: 'hold' });

  if (hasMultiple.value) {
    options.push(
      {
        label: i18n.t('coverage.actionBillEarlier'),
        icon: 'arrow_upward',
        value: 'up',
        disable: row.isFirst,
      },
      {
        label: i18n.t('coverage.actionBillLater'),
        icon: 'arrow_downward',
        value: 'down',
        disable: row.isLast,
      },
    );
  }

  options.push({
    group: 'destructive',
    options: [{
      label: i18n.t('common.buttonDelete'), icon: 'delete', value: 'remove', destructive: true,
    }],
  });

  return options;
} // menuOptions()

function onSelect(row, option) {
  switch (option.value) {
    case 'edit':
      emit('edit', row.coverage);
      break;
    case 'verify':
      emit('verify', { coverageId: row.coverage.id, verified: true });
      break;
    case 'unverify':
      emit('verify', { coverageId: row.coverage.id, verified: false });
      break;
    case 'hold':
      emit('pause', { coverageId: row.coverage.id, paused: true });
      break;
    case 'release':
      emit('pause', { coverageId: row.coverage.id, paused: false });
      break;
    case 'up':
      emit('move', { id: row.id, offset: -1 });
      break;
    case 'down':
      emit('move', { id: row.id, offset: 1 });
      break;
    case 'remove':
      emit('remove', row.coverage);
      break;
    default:
      break;
  }
} // onSelect()

function onReorder(reorderedRows) {
  emit('reorder', reorderedRows.map((row) => row.id));
} // onReorder()
</script>

<template>
  <hr-sortable-list
    :model-value="rows"
    item-key="id"
    :disabled="disable || !hasMultiple"
    long-press-body
    @update:model-value="onReorder"
  >
    <template #item="{ element }">
      <hr-sortable-item
        segmented
        :label="element.label"
        :caption="element.caption"
        clickable
        :disable="disable"
        data-e2e="list-item-coverage"
        @click="emit('edit', element.coverage)"
      >
        <template #trailing>
          <div class="row items-center no-wrap q-gutter-sm">
            <!-- A problem gets one icon, whichever problem it is. Three sentences competing for the
                 same short slot is what squeezed the payer's name to three lines, and which problem
                 it is has room to be explained inside the coverage. Not-yet states keep their words:
                 an upcoming coverage is not something to warn about.

                 NO aria-label HERE, and its absence is not an oversight. QIcon renders
                 aria-hidden="true" and role="presentation" unconditionally, so a label on it is
                 never announced, and QTooltip opens on pointer and touch but not on focus. One that
                 looked right would only hide the fact that this status has no non-visual route at
                 all. Carrying a semantic label is HrIcon's to offer and it does not yet. -->
            <hr-icon
              v-if="element.isProblem"
              class="coverage-list__warning hr-text-warning"
              name="warning"
              size="sm"
              :data-e2e="`coverage-attention-${element.attention}`"
            >
              <hr-tooltip>{{ element.statusLabel }}</hr-tooltip>
            </hr-icon>
            <hr-item-label
              v-else-if="element.statusLabel !== null"
              caption
              class="coverage-list__status"
            >
              {{ element.statusLabel }}
            </hr-item-label>
            <coverage-rank-chip
              :variant="element.rankVariant"
              :number="element.orderNumber"
            />
            <hr-overflow-menu-btn
              :options="menuOptions(element)"
              :disable="disable"
              @click.stop
              @select="onSelect(element, $event)"
            />
          </div>
        </template>
      </hr-sortable-item>
    </template>
  </hr-sortable-list>
</template>

<style lang="scss" scoped>
.coverage-list__status {
  white-space: nowrap;
}
</style>
